import { motion } from "framer-motion";
import { Globe, Palette, Box, Film } from "lucide-react";

const primaryService = {
  icon: Globe,
  title: "Websites for Local Businesses",
  description:
    "Your business deserves more than a template. I design and build fast, beautiful, custom websites that actually bring in customers. From coffee shops to contractors, I make your online presence match your real-world quality.",
  color: "bg-[#FE4A90]",
  textColor: "text-white",
};

const secondaryServices = [
  {
    icon: Palette,
    title: "Brand & Logo Design",
    description: "Cohesive visual identities that stick in people's heads.",
    color: "bg-[#FFD166]",
    textColor: "text-[#0A0A0A]",
  },
  {
    icon: Box,
    title: "3D & Archviz",
    description: "Photorealistic 3D renders and architectural visualizations.",
    color: "bg-[#06D6A0]",
    textColor: "text-[#0A0A0A]",
  },
  {
    icon: Film,
    title: "Motion & Animation",
    description: "Motion graphics, animations, and video content that moves people.",
    color: "bg-[#0A0A0A]",
    textColor: "text-[#FFFDF5]",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#FFFDF5] section-divider"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <p className="font-mono text-sm uppercase tracking-widest font-bold text-[#FE4A90] mb-3">
          What I Do
        </p>
        <h2
          data-testid="services-title"
          className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A0A0A] uppercase"
        >
          Services
        </h2>
      </motion.div>

      {/* Primary service - big card */}
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.1, ease: [0.34, 1.56, 0.64, 1] }}
        className="mt-14"
      >
        <div
          data-testid="service-primary"
          className={`brutal-card ${primaryService.color} p-8 md:p-12`}
        >
          <div className="flex items-start gap-4 md:gap-6">
            <div className="brutal-card bg-[#FFFDF5] p-3 flex-shrink-0">
              <primaryService.icon size={32} className="text-[#0A0A0A]" />
            </div>
            <div>
              <h3 className={`font-heading text-2xl sm:text-3xl tracking-tight ${primaryService.textColor}`}>
                {primaryService.title}
              </h3>
              <p className={`font-body text-base sm:text-lg mt-4 leading-relaxed max-w-2xl ${primaryService.textColor} opacity-90`}>
                {primaryService.description}
              </p>
              <a
                href="#contact"
                data-testid="service-primary-cta"
                className="brutal-btn bg-[#FFFDF5] text-[#0A0A0A] px-6 py-3 text-sm inline-block mt-8"
              >
                Start a Project
              </a>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Secondary services */}
      <p className="font-mono text-sm uppercase tracking-widest font-bold text-[#FE4A90] mt-8 mb-4 px-1">
        Additional Services
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {secondaryServices.map((service, i) => (
          <motion.div
            key={service.title}
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{
              duration: 0.5,
              delay: 0.15 + i * 0.1,
              ease: [0.34, 1.56, 0.64, 1],
            }}
          >
            <div
              data-testid={`service-secondary-${i + 1}`}
              className={`brutal-card ${service.color} p-6 md:p-8 h-full`}
            >
              <service.icon size={28} className={service.textColor} />
              <h4 className={`font-heading text-xl sm:text-2xl mt-4 ${service.textColor}`}>
                {service.title}
              </h4>
              <p className={`font-body text-sm mt-3 leading-relaxed ${service.textColor} opacity-80`}>
                {service.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
